import Layout from '../../components/Layout';
import RoutineStep from '../../components/RoutineStep';
import DisclaimerNotice from '../../components/DisclaimerNotice';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';

const routineSteps = [
  {
    stepNumber: 1,
    title: "Double Cleanse (Oil-Based)",
    description: "Start with an oil-based cleanser to dissolve makeup, sunscreen, and excess sebum. This prepares your skin for a deeper cleanse.",
    products: [
      {
        name: "Cleansing Oil",
        description: "Gentle oil cleanser that emulsifies and rinses clean.",
        bestFor: "All Skin Types",
        benefits: ["Removes makeup effectively", "Non-comedogenic", "Rinses clean without residue"]
      },
      {
        name: "Cleansing Balm",
        description: "A richer texture that melts into skin for thorough cleansing.",
        bestFor: "Dry & Normal Skin",
        benefits: ["Luxurious texture", "Deeply cleansing", "Nourishing ingredients"]
      }
    ]
  },
  {
    stepNumber: 2,
    title: "Double Cleanse (Water-Based)",
    description: "Follow with a water-based cleanser to remove any remaining impurities and ensure your skin is truly clean.",
    products: [
      {
        name: "Cetaphil Gentle Skin Cleanser (473ml)",
        description: "A gentle, fragrance-free cleanser that helps cleanse the skin without stripping moisture. Especially suitable for sensitive and acne-prone skin where maintaining the skin barrier is important.",
        bestFor: "Sensitive & Acne-Prone Skin",
        benefits: ["Fragrance-free formula", "Maintains skin barrier", "Gentle daily cleansing"],
        imageUrl: "https://www.cetaphil.com/dw/image/v2/BGGN_PRD/on/demandware.static/-/Sites-galderma-us-m-catalog/default/dw554054cc/302990100624/GSC-RESIZE-1.2X.png?sw=600&sh=600&sm=fit&q=85",
        affiliateLink: "https://amzn.to/4apJ67b"
      },
      {
        name: "Gentle Foaming Cleanser",
        description: "A pH-balanced, non-stripping cleanser for the second cleanse.",
        bestFor: "All Skin Types",
        benefits: ["Gentle formula", "pH-balanced", "Removes remaining residue"]
      }
    ]
  },
  {
    stepNumber: 3,
    title: "Exfoliate (2-3x per week)",
    description: "Exfoliation removes dead skin cells and allows your other products to penetrate better. Don't overdo it—2-3 times per week is usually sufficient.",
    products: [
      {
        name: "AHA/BHA Toner",
        description: "A gentle chemical exfoliant with glycolic and salicylic acids.",
        bestFor: "Most Skin Types",
        benefits: ["Gentle exfoliation", "Refines texture", "Unclogs pores"]
      },
      {
        name: "Enzyme Exfoliant",
        description: "A gentler option using fruit enzymes for sensitive skin.",
        bestFor: "Sensitive Skin",
        benefits: ["Ultra-gentle", "No harsh acids", "Brightening effect"]
      }
    ]
  },
  {
    stepNumber: 4,
    title: "Treatment Serum",
    description: "Night is the ideal time for active treatments. Choose based on your concerns—retinol for anti-aging, niacinamide for pores, or hyaluronic acid for hydration.",
    products: [
      {
        name: "Retinol Serum (0.3%)",
        description: "A beginner-friendly retinol for anti-aging and skin renewal.",
        bestFor: "Anti-Aging & Texture",
        benefits: ["Promotes cell turnover", "Reduces fine lines", "Beginner-friendly strength"]
      },
      {
        name: "Niacinamide Serum (10%)",
        description: "Addresses pores, oil control, and uneven skin tone.",
        bestFor: "Oily & Combination Skin",
        benefits: ["Minimizes pores", "Controls oil", "Evens skin tone"]
      },
      {
        name: "Hyaluronic Acid Serum",
        description: "Intense hydration for all skin types.",
        bestFor: "All Skin Types",
        benefits: ["Deep hydration", "Plumping effect", "Lightweight formula"]
      }
    ]
  },
  {
    stepNumber: 5,
    title: "Eye Cream (Optional)",
    description: "The delicate eye area can benefit from targeted care. Look for gentle, hydrating formulas.",
    products: [
      {
        name: "Hydrating Eye Cream",
        description: "A gentle formula to hydrate and protect the eye area.",
        bestFor: "All Skin Types",
        benefits: ["Gentle for eye area", "Reduces puffiness", "Hydrates fine lines"]
      }
    ]
  },
  {
    stepNumber: 6,
    title: "Moisturize",
    description: "Seal in all your treatments with a nourishing night cream. You can use a richer formula at night than you would during the day.",
    products: [
      {
        name: "Nourishing Night Cream",
        description: "A rich, restorative cream for overnight repair.",
        bestFor: "Normal & Dry Skin",
        benefits: ["Deep nourishment", "Overnight repair", "Rich texture"]
      },
      {
        name: "Lightweight Night Gel",
        description: "A lighter option for oily skin that still provides hydration.",
        bestFor: "Oily & Combination Skin",
        benefits: ["Oil-free formula", "Non-comedogenic", "Adequate hydration"]
      }
    ]
  }
];

const EveningRoutines = () => {
  return (
    <Layout>
      {/* Hero */}
      <section data-testid="evening-routine-hero" className="py-20 md:py-32 bg-[#F2EBE3]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Link 
            to="/skincare" 
            className="inline-flex items-center gap-2 text-[#3A4D39] text-sm font-medium mb-6 hover:underline"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Skincare
          </Link>
          <p className="text-xs tracking-widest uppercase font-medium text-[#C88D74] mb-4">
            Skincare Routine
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#1C1C1C] leading-tight mb-6">
            Evening Skincare Routine
          </h1>
          <p className="text-base md:text-lg text-[#374151] leading-relaxed mb-6">
            Your skin repairs itself while you sleep. This evening routine focuses on thorough cleansing, active treatments, and deep nourishment for overnight renewal.
          </p>
          <div className="flex flex-wrap gap-2">
            <span className="px-3 py-1 bg-white text-[#374151] text-xs font-medium">Intermediate</span>
            <span className="px-3 py-1 bg-white text-[#374151] text-xs font-medium">6 Steps</span>
            <span className="px-3 py-1 bg-white text-[#374151] text-xs font-medium">All Skin Types</span>
          </div>
        </div>
      </section>

      {/* Routine Steps */}
      <section data-testid="evening-routine-steps" className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-4">
            Your Evening Routine
          </h2>
          <p className="text-[#374151] mb-8">
            This routine is more thorough than the morning—your skin has all night to absorb and benefit from active ingredients.
          </p>
          
          <div className="divide-y divide-[#E5E5E5]">
            {routineSteps.map((step) => (
              <RoutineStep key={step.stepNumber} {...step} />
            ))}
          </div>
        </div>
      </section>

      {/* Tips Section */}
      <section data-testid="evening-routine-tips" className="py-16 md:py-24 bg-[#F5F5F0]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-6">
            Tips & Notes
          </h2>
          <ul className="space-y-4 text-[#374151]">
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Double cleansing:</strong> Essential if you wear makeup or sunscreen. You can skip the oil cleanser on bare-skin days.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Retinol caution:</strong> If you're new to retinol, start 1-2x per week and gradually increase. Always use sunscreen the next day.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Don't mix:</strong> Avoid using retinol and AHA/BHA on the same night—alternate them.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Silk pillowcase:</strong> Consider sleeping on a silk pillowcase to reduce friction and product transfer.</span>
            </li>
          </ul>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <DisclaimerNotice />
        </div>
      </section>

      {/* Next Guide CTA */}
      <section className="py-16 md:py-20 bg-[#F2EBE3]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <p className="text-xs tracking-widest uppercase font-medium text-[#6B7280] mb-2">Related Guide</p>
          <h3 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-4">
            Acne-Prone Skin Guide
          </h3>
          <p className="text-[#374151] mb-6">
            Struggling with breakouts? Our acne guide covers gentle yet effective products for clearer skin.
          </p>
          <Link 
            to="/skincare/acne"
            data-testid="next-guide-cta"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#3A4D39] text-white text-sm font-medium tracking-wide uppercase transition-all duration-300 hover:bg-[#2A3D29]"
          >
            View Acne Guide
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default EveningRoutines;
